<template>
  <v-stepper
    v-model="currentStep"
    class="d-flex flex-column"
    elevation="0"
    height="100%"
    data-testid="raster-download-stepper"
  >
    <v-stepper-header class="height-56">
      <div class="pl-8 d-flex align-center">
        {{ datasetStore.metadata?.title }}
      </div>
      <v-stepper-item
        :complete="currentStep > 1"
        :value="1"
        color="primary"
      >
        {{ rasterSelectionTitle }}
      </v-stepper-item>
      <v-stepper-item
        :value="2"
        color="primary"
      >
        {{
          t('download_form.choose.parameter') +
            ` (${parameterStore.selectedParameters.length}) ` +
            t('and') + ' ' + t('download_form.to_download')
        }}
      </v-stepper-item>
    </v-stepper-header>
    <v-stepper-window
      v-model="currentStep"
      class="fill-height ma-0"
    >
      <v-stepper-window-item
        class="fill-height pa-4"
        :value="1"
      >
        <keep-alive>
          <raster-selection-page
            v-if="currentStep === 1"
            ref="rasterSelectionPage"
            :default-type="defaultType"
            @on-title-change="(title) => (rasterSelectionTitle = title)"
            @on-raster-type-changed="handleRasterTypeChange"
          />
        </keep-alive>
      </v-stepper-window-item>
      <v-stepper-window-item
        class="fill-height pa-4"
        :value="2"
      >
        <keep-alive>
          <raster-download-page
            v-if="currentStep === 2"
            ref="downloadPage"
            :raster-type="rasterType"
          />
        </keep-alive>
      </v-stepper-window-item>
    </v-stepper-window>
    <stepper-navigation
      :hide-next="hideNextButton"
      :hide-previous="hidePreviousButton"
      :allow-next="enableNextButton"
      @previous="previousStep"
      @next="nextStep"
      @abort="abort"
    />
  </v-stepper>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import DatasetStore from '@/stores/dataset/dataset-module';
import ParameterStore from '@/stores/parameter/parameter-module';
import TimeseriesStore from '@/stores/timeseries/timeseries-module';
import GridStore from '@/stores/grid/grid-module';
import StepperNavigation from '@/components/common/StepperNavigation.vue';
import RasterSelectionPage, { RasterType } from '@/components/raster/RasterSelectionPage.vue';
import RasterDownloadPage from '@/components/raster/RasterDownloadPage.vue';
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: 'RasterDownloadStepper',
  components: {
    StepperNavigation,
    RasterSelectionPage,
    RasterDownloadPage,
  },
  props: {
    defaultType: { type: String, required: true },
  },
  setup(props) {
    const { t } = useI18n();

    const datasetStore = DatasetStore;
    const parameterStore = ParameterStore;
    const timeseriesStore = TimeseriesStore;
    const gridStore = GridStore;

    const currentStep = ref(1);
    const rasterSelectionTitle = ref('');
    const rasterType = ref<RasterType | null>(
      props.defaultType === 'grid' ? RasterType.GRID : RasterType.TIMESERIES
    );

    const rasterSelectionPage = ref<InstanceType<typeof RasterSelectionPage> | null>(null);
    const downloadPage = ref<InstanceType<typeof RasterDownloadPage> | null>(null);

    const minSteps = 1;
    const maxSteps = 2;

    const parametersAreSelected = computed<boolean>(() => {
      return parameterStore.selectedParameters.length > 0;
    });

    const hidePreviousButton = computed<boolean>(() => {
      return currentStep.value <= minSteps;
    });
    const hideNextButton = computed<boolean>(() => {
      return currentStep.value >= maxSteps;
    });

    const enableNextButton = computed<boolean>(() => {
      if (currentStep.value === 1) {
        if (rasterType.value === RasterType.TIMESERIES) {
          return Object.keys(timeseriesStore.activeCoords).length !== 0;
        } else if (rasterType.value === RasterType.GRID) {
          return gridStore.isValidBbox;
        }
        return gridStore.isValidBbox;
      } else if (currentStep.value === 2) {
        return parametersAreSelected.value;
      }
      return true;
    });

    function abort() {
      downloadPage.value?.parameterStore.updateSelectedParameters([]);
      timeseriesStore.resetCoords();
      gridStore.resetBbox();
      currentStep.value = 1;
    };

    function nextStep() {
      if (currentStep.value < maxSteps) {
        currentStep.value += 1;
      }
    };

    function previousStep() {
      if (currentStep.value > minSteps) {
        currentStep.value -= 1;
      }
    };

    function handleRasterTypeChange(newType: RasterType) {
      rasterType.value = newType;
    }

    return {
      t,
      datasetStore,
      parameterStore,
      timeseriesStore,
      gridStore,
      currentStep,
      rasterSelectionTitle,
      rasterType,
      downloadPage,
      rasterSelectionPage,
      hidePreviousButton,
      hideNextButton,
      enableNextButton,
      abort,
      nextStep,
      previousStep,
      handleRasterTypeChange,
    };
  },
});
</script>

<style scoped>
.height-56 {
  height: 56px;
  min-height: 56px;
  max-height: 56px;
}

:deep(.v-stepper__step) {
  /*overwrite padding inside of stepper header to make sure that text is centered vertically*/
  padding: 0 24px 0 24px;
}

:deep(.v-stepper__wrapper) {
  /*scale stepper to full available height */
  height: 100%;
}

:deep(.v-stepper__wrapper),
.v-stepper__items {
  /*Needed to show the box-shadows of cards in the stepper  */
  overflow: visible;
}

:deep(.v-stepper-item--complete .v-stepper-item__avatar .v-icon) {
  color: rgb(var(--v-theme-on-primary)) !important; /* icon color */
}

:deep(.v-stepper-header) {
  background-color: rgb(var(--v-theme-secondary));
  box-shadow: none !important;
}
</style>
